import React, { useState, useEffect, useRef, ReactElement } from 'react';
import PropTypes from 'prop-types';
import { styler, tween } from 'popmotion';
import { usePrevious } from '../../utils/customHooks';

import Button from './Button';
import Loading from '../Loading';
import { ButtonAction, ButtonType } from './types/Button';

interface ContextualButtonProps {
  id: string;
  buttonText?: string;
  buttonTextOnClick?: string;
  buttonType?: ButtonType;
  onClick?: any;
  showLoadingIndicator: boolean;
  loading?: boolean;
  type?: ButtonAction;
  disabled?: boolean;
  buttonWrapperStyle?: React.CSSProperties;
  customStyle?: React.CSSProperties;
  isDefault?: boolean;
}

const ContextualButton = ({
  id,
  buttonText,
  buttonTextOnClick,
  buttonType,
  onClick,
  showLoadingIndicator,
  loading,
  buttonWrapperStyle,
  ...rest
}: ContextualButtonProps): ReactElement => {
  const buttonRef = useRef();
  const prevLoading = usePrevious(loading);
  const [initButtonWidth, setInitButtonWidth] = useState(0);
  const [animatedButtonWidth, setAnimatedButtonWidth] = useState(0);

  useEffect(() => {
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    const ghostButton = buttonRef.current.querySelector('#ghost-button');
    const newAnimatedButtonWidth = ghostButton.offsetWidth;

    ghostButton.style.display = 'none';

    setAnimatedButtonWidth(newAnimatedButtonWidth);
    setTimeout(() => ghostButton.parentNode.removeChild(ghostButton), 300);
  }, []);

  useEffect(() => {
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    setInitButtonWidth(buttonRef.current.querySelector(`#loading-button-${id}`).offsetWidth);
  }, [id]);

  useEffect(() => {
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    const buttonEl = buttonRef.current.querySelector(`#loading-button-${id}`);
    const button = styler(buttonEl);

    if (!!prevLoading !== loading) {
      tween({
        from: { width: loading ? initButtonWidth : animatedButtonWidth },
        to: { width: loading ? animatedButtonWidth : initButtonWidth },
        duration: 800,
      }).start(button.set);
    }
  }, [loading, animatedButtonWidth, id, initButtonWidth, prevLoading]);

  const transitionButton = (
    <div style={{ position: 'relative', display: 'flex' }}>
      {buttonTextOnClick || buttonText}

      {showLoadingIndicator && (
        <div style={{ position: 'absolute', right: '-28px', top: '0' }}>
          <Loading width={18} color={buttonType === 'primary' ? '#FFF' : 'rgba(0,0,0,0.3)'} />
        </div>
      )}
    </div>
  );

  return (
    <div ref={buttonRef} style={{ width: '100%', ...buttonWrapperStyle }}>
      <Button
        id={`loading-button-${id}`}
        onClick={onClick}
        buttonType={buttonType || 'primaryRed'}
        iconPosition="right"
        alignContent="left"
        customStyle={{ position: 'relative', overflow: 'hidden' }}
        {...rest}
      >
        {loading ? transitionButton : buttonText}
      </Button>

      <Button id="ghost-button" disabled>
        {buttonTextOnClick || buttonText}

        {showLoadingIndicator && (
          <div style={{ paddingLeft: '10px' }}>
            <Loading width={18} />
          </div>
        )}
      </Button>
    </div>
  );
};

ContextualButton.defaultProps = {
  showLoadingIndicator: true,
};

ContextualButton.propTypes = {
  id: PropTypes.string.isRequired,
  /** Button content */
  buttonText: PropTypes.any.isRequired,
  /** What content will animate to; will default to buttonText if no defined */
  buttonTexOnClick: PropTypes.string,
  /** Show loading icon next to buttonText */
  showLoadingIndicator: PropTypes.bool,
  /** If true, animates to buttonTextOnClick; if false, animates to buttonText */
  loading: PropTypes.bool,
};

export default ContextualButton;
